/**
 * Pure helpers for the Skip or Always 0 card (`PushSkuRulesSection`,
 * `PushRuleSearchPanel`, `PushRulesList`, `PasteSkuListDialog`,
 * `AlwaysZeroConfirmDialog`, `RemoveRulesConfirmDialog`).
 *
 * The rule list is capped server-side (`PUSH_SKU_RULES_CAP` ids) and arrives
 * sorted by SKU, so the client filters, counts, groups and pages it in memory:
 * the filter, the
 * counts and the pager all work on the SAME array, which is what CLAUDE.md's
 * "filter, count and paginate at the same layer" rule asks for.
 *
 * Shapes are read off `RouterOutputs` so they track the backend; the imports
 * are type-only, so the helpers stay testable on their own.
 */
import type { TFunction } from 'i18next';
import { alwaysZeroCeiling } from '@threadheads/shared';
import { sharedName } from '@/features/inventory/utils/supplyAliasGroups';
import type { RouterOutputs } from '@/lib/trpc';

type StockSyncOutputs = RouterOutputs['stockSync'];

export type PushRuleList = StockSyncOutputs['listPushSkuRules'];
export type PushRuleRow = PushRuleList['rules'][number];
export type PushRuleSearchResult = StockSyncOutputs['searchPushRuleSkus']['results'][number];
export type PreviewResult = StockSyncOutputs['previewPushSkuRules'];
export type PreviewMatch = PreviewResult['matched'][number];
export type PreviewUnmatched = PreviewResult['unmatched'][number];
export type SetPushSkuRulesResult = StockSyncOutputs['setPushSkuRules'];
export type PreviousRule = SetPushSkuRulesResult['previous'][number];

export type PushSkuRule = PushRuleRow['rule'];
export type PushSkuRuleOrNone = PushSkuRule | 'none';
export type RuleFilter = 'all' | PushSkuRule;
/** 'ok', or what is special about a stored rule's SKU (see `isInertStatus`). */
export type PushRuleStatus = PushRuleRow['status'];
export type UnmatchedReason = PreviewUnmatched['reason'];

/** SKUs per page in the rules table (a bigger product is split across pages). */
export const RULES_PAGE_SIZE = 50;
/** Lines the paste dialog accepts in one go (the preview input cap). */
export const PASTE_LINE_LIMIT = 2000;
/** Longest code the preview accepts; a longer line can't be a SKU or handle. */
export const PASTE_CODE_MAX_LENGTH = 200;
/** Search input bounds (the `searchPushRuleSkus` contract). */
export const SEARCH_MIN_LENGTH = 2;
export const SEARCH_MAX_LENGTH = 100;
/** Rows a preview list renders before it says "and N more" (DESIGN.md section 14). */
export const PREVIEW_VISIBLE_ROWS = 200;

/** Statuses whose stored rule the push still applies, so the row is live. */
const LIVE_STATUSES: ReadonlySet<PushRuleStatus> = new Set<PushRuleStatus>(['ok', 'retired', 'preprinted']);

/**
 * Whether a stored rule does nothing any more. Two non-ok statuses are NOT
 * inert, because the push still applies the rule: `retired` (to the retired
 * SKU's stock) and `preprinted` (to the design's own stock rows only, so it
 * partly applies; the full rule belongs on the base garment). Their rows stay
 * live: Remove asks first when one is Always 0, and "Remove these N" never
 * sweeps them. Every other status (deleted, not found, a blank's stock line,
 * not POD or PAP) does nothing. A client's copy of a 3PL garment is a plain
 * live row: the push sends the 3PL's pool figure for it, and both rules apply.
 */
export function isInertStatus(status: PushRuleStatus): boolean {
  return !LIVE_STATUSES.has(status);
}

/** Rules that do nothing any more; they still count toward the cap. */
export function inertRuleIds(rows: readonly PushRuleRow[]): string[] {
  return rows.filter((row) => isInertStatus(row.status)).map((row) => row.skuId);
}

/** Left-out paste lines that point at a base garment to set instead. */
const BASE_GARMENT_OFFER_REASONS: ReadonlySet<UnmatchedReason> = new Set<UnmatchedReason>([
  'bare_blank',
  'preprinted',
]);

/**
 * The base garment a left-out paste line offers ("Add base garment"): a
 * blank's own stock line and a pre-printed design both point at it. null when
 * the line offers none.
 */
export function baseGarmentOffer(line: Pick<PreviewUnmatched, 'reason' | 'baseGarmentSkuId'>): string | null {
  return BASE_GARMENT_OFFER_REASONS.has(line.reason) ? line.baseGarmentSkuId : null;
}

/** The one type a SKU shows: Base garment beats its POD / PAP code; anything else has none. */
export type SkuKind = 'base_garment' | 'POD' | 'PAP';

export function skuKind(row: { productionType: string | null; isBaseGarment: boolean }): SkuKind | null {
  if (row.isBaseGarment) return 'base_garment';
  return row.productionType === 'POD' || row.productionType === 'PAP' ? row.productionType : null;
}

/**
 * The variant part of a SKU title: "Classic Tee - S / Black" under the product
 * "Classic Tee" reads "S / Black". A title that does not start with the
 * product's name is kept whole; no title at all is null.
 */
export function variantLabel(title: string | null, productTitle: string | null): string | null {
  if (!title) return null;
  if (!productTitle) return title;
  const prefix = `${productTitle} - `;
  if (title.length > prefix.length && title.toLowerCase().startsWith(prefix.toLowerCase())) {
    return title.slice(prefix.length);
  }
  return title === productTitle ? null : title;
}

function matchesQuery(row: PushRuleRow, needle: string): boolean {
  if (!needle) return true;
  return [row.sku, row.title, row.productTitle].some(
    (field) => typeof field === 'string' && field.toLowerCase().includes(needle),
  );
}

export interface RuleView {
  /** Rows matching the filter text AND the rule filter, in the server's order. */
  rows: PushRuleRow[];
  /**
   * Counts of the rows matching the FILTER TEXT, per rule, so each segment
   * says exactly how many rows pressing it will show.
   */
  counts: { all: number; skip: number; alwaysZero: number };
}

export function filterRules(rows: readonly PushRuleRow[], query: string, filter: RuleFilter): RuleView {
  const needle = query.trim().toLowerCase();
  const searched = rows.filter((row) => matchesQuery(row, needle));
  let skip = 0;
  let alwaysZero = 0;
  for (const row of searched) {
    if (row.rule === 'skip') skip += 1;
    else alwaysZero += 1;
  }
  const narrowed = filter === 'all' ? searched : searched.filter((row) => row.rule === filter);
  return { rows: narrowed, counts: { all: searched.length, skip, alwaysZero } };
}

export type GroupRuleSummary = 'always_zero' | 'skip' | 'mixed';

export interface RuleGroup {
  /** Stable key: `p:<productId>`, else `g:<base garment code>` (3PL garments), else `t:<product name>` (`t:` alone for no product). */
  key: string;
  /** null when the row carries no product id (a rule whose SKU is gone, retired, and so on). */
  productId: string | null;
  /**
   * The base garment code of a 3PL garment group (a client's copies of its
   * 3PL's garments have no product, so they group by this instead); else null.
   */
  garmentCode: string | null;
  /** The product's (or 3PL garment's) name; null for an untitled product or no product at all. */
  productTitle: string | null;
  rows: PushRuleRow[];
  summary: GroupRuleSummary;
}

/** Titled products first, then untitled ones, then the rows with no product. */
function groupRank(group: Pick<RuleGroup, 'productId' | 'productTitle'>): number {
  if (group.productTitle !== null) return 0;
  return group.productId !== null ? 1 : 2;
}

/**
 * A 3PL garment's name: the part of its copies' titles before " - " ("Classic
 * Boxy Crop Tee - Black / L" is "Classic Boxy Crop Tee", the rule the Products
 * page uses), else a title they all share, else the code.
 */
export function garmentName(titles: ReadonlyArray<string | null>, code: string): string {
  const shared = sharedName(titles.map((title) => ({ title })));
  if (shared) return shared;
  const first = titles[0];
  return first && titles.every((title) => title === first) ? first : code;
}

/**
 * The rules grouped by product id (a 3PL garment's copies, which have no
 * product, by base garment code), groups by name and the no-product group
 * last; each group keeps the server's SKU order. Two products that share a
 * name stay two groups (their keys differ). Only a row with no product id and
 * no garment code falls back to its product name, so such rows still sit
 * together.
 */
export function groupRules(rows: readonly PushRuleRow[]): RuleGroup[] {
  const garmentCodeOf = (row: PushRuleRow): string | null => (row.productId ? null : row.garmentCode);
  const byKey = new Map<string, PushRuleRow[]>();
  for (const row of rows) {
    const garmentCode = garmentCodeOf(row);
    const key = row.productId ? `p:${row.productId}` : garmentCode ? `g:${garmentCode}` : `t:${row.productTitle ?? ''}`;
    const list = byKey.get(key);
    if (list) list.push(row);
    else byKey.set(key, [row]);
  }
  const groups: RuleGroup[] = [...byKey.entries()].map(([key, groupRows]) => {
    const first = groupRows[0]!;
    const rulesHere = new Set(groupRows.map((row) => row.rule));
    const garmentCode = garmentCodeOf(first);
    return {
      key,
      productId: first.productId ?? null,
      garmentCode,
      productTitle: garmentCode ? garmentName(groupRows.map((row) => row.title), garmentCode) : first.productTitle,
      rows: groupRows,
      summary: rulesHere.size > 1 ? 'mixed' : first.rule,
    };
  });
  return groups.sort(
    (a, b) =>
      groupRank(a) - groupRank(b) ||
      (a.productTitle ?? '').localeCompare(b.productTitle ?? '') ||
      a.key.localeCompare(b.key),
  );
}

export interface GroupSlice {
  group: RuleGroup;
  /** The group's rows shown on this page (all of them unless the group is split). */
  rows: PushRuleRow[];
  /** The group started on an earlier page. */
  continued: boolean;
}

export interface GroupPage {
  slices: GroupSlice[];
  /** Zero-based page actually shown (clamped into range). */
  page: number;
  pageCount: number;
  /** One-based SKU positions, inclusive; 0 and 0 when there are no rows. */
  from: number;
  to: number;
  total: number;
}

/**
 * Pages by group: a page holds whole groups up to `size` SKUs, so a product is
 * never cut in two while it fits on a page. A product with more SKUs than a
 * page is split, its header repeated on each page, so no page renders more
 * than `size` rows (DESIGN.md section 14).
 */
export function paginateGroups(groups: readonly RuleGroup[], page: number, size = RULES_PAGE_SIZE): GroupPage {
  const pages: GroupSlice[][] = [];
  let current: GroupSlice[] = [];
  let used = 0;
  const flush = () => {
    if (current.length > 0) pages.push(current);
    current = [];
    used = 0;
  };
  for (const group of groups) {
    if (group.rows.length <= size) {
      if (used + group.rows.length > size) flush();
      current.push({ group, rows: group.rows, continued: false });
      used += group.rows.length;
      continue;
    }
    flush();
    for (let start = 0; start < group.rows.length; start += size) {
      const rows = group.rows.slice(start, start + size);
      if (rows.length === size) pages.push([{ group, rows, continued: start > 0 }]);
      else {
        current = [{ group, rows, continued: start > 0 }];
        used = rows.length;
      }
    }
  }
  flush();

  const total = groups.reduce((sum, group) => sum + group.rows.length, 0);
  const pageCount = Math.max(1, pages.length);
  const clamped = Math.min(Math.max(0, Math.floor(page)), pageCount - 1);
  const slices = pages[clamped] ?? [];
  let before = 0;
  for (let i = 0; i < clamped; i += 1) {
    for (const slice of pages[i]!) before += slice.rows.length;
  }
  const shown = slices.reduce((sum, slice) => sum + slice.rows.length, 0);
  return {
    slices,
    page: clamped,
    pageCount,
    from: total === 0 ? 0 : before + 1,
    to: before + shown,
    total,
  };
}

export interface SearchGarment {
  /** The base garment code the group's copies share (the first segment of their SKUs). */
  code: string;
  /** See `garmentName`. */
  name: string;
  /** How many live copies of the garment the org holds (a pick brings in all of them). */
  skuCount: number;
}

export interface SearchGroup {
  /** null for SKUs with no product: they render without a product header. */
  product: NonNullable<PushRuleSearchResult['product']> | null;
  /** A 3PL garment's copies group here instead of by product; null for everything else. */
  garment: SearchGarment | null;
  results: PushRuleSearchResult[];
}

/**
 * Search results grouped by product, else (a 3PL garment's copy has no
 * product) by base garment code, in the order the server returned them. A SKU
 * with neither stays on its own, without a header.
 */
export function groupSearchResults(results: readonly PushRuleSearchResult[]): SearchGroup[] {
  const groups: SearchGroup[] = [];
  const byProduct = new Map<string, SearchGroup>();
  const byGarment = new Map<string, SearchGroup>();
  for (const result of results) {
    const { product, garment } = result;
    if (!product && !garment) {
      groups.push({ product: null, garment: null, results: [result] });
      continue;
    }
    const lookup = product ? byProduct : byGarment;
    const id = product ? product.id : garment!.code;
    const existing = lookup.get(id);
    if (existing) {
      existing.results.push(result);
      continue;
    }
    const group: SearchGroup = {
      product,
      garment: !product && garment ? { ...garment, name: garment.code } : null,
      results: [result],
    };
    lookup.set(id, group);
    groups.push(group);
  }
  for (const group of byGarment.values()) {
    group.garment = { ...group.garment!, name: garmentName(group.results.map((r) => r.title), group.garment!.code) };
  }
  return groups;
}

/** What a header's [Skip] / [Always 0] acts on: every SKU of a product, or of a 3PL garment. */
export type GroupTarget = { kind: 'product'; id: string } | { kind: 'garment'; code: string };

/** The `previewPushSkuRules` input that names the whole group. */
export function groupPreviewInput(target: GroupTarget): { productIds: string[] } | { garmentCodes: string[] } {
  return target.kind === 'product' ? { productIds: [target.id] } : { garmentCodes: [target.code] };
}

/** In-flight key of a group change (`product:<id>` / `garment:<code>`), as `SearchPending.key`. */
export function groupTargetKey(target: GroupTarget): string {
  return target.kind === 'product' ? `product:${target.id}` : `garment:${target.code}`;
}

export interface ParsedPaste {
  /** Trimmed, non-empty, de-duplicated (case-insensitive, first spelling kept). */
  codes: string[];
  /** Non-empty entries before de-duplication. */
  entryCount: number;
  duplicates: number;
  /** Entries longer than the preview accepts, left out (one bad line never fails the rest). */
  tooLong: number;
  overLimit: boolean;
}

/**
 * One SKU or product handle per line. Commas, tabs and semicolons split too,
 * so a row copied out of a spreadsheet works without cleaning it first.
 */
export function parsePastedCodes(text: string, limit = PASTE_LINE_LIMIT): ParsedPaste {
  const entries = text
    .split(/[\r\n,\t;]+/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
  const seen = new Set<string>();
  const codes: string[] = [];
  let tooLong = 0;
  for (const entry of entries) {
    if (entry.length > PASTE_CODE_MAX_LENGTH) {
      tooLong += 1;
      continue;
    }
    const key = entry.toUpperCase();
    if (seen.has(key)) continue;
    seen.add(key);
    codes.push(entry);
  }
  return {
    codes,
    entryCount: entries.length,
    duplicates: entries.length - tooLong - codes.length,
    tooLong,
    overLimit: codes.length > limit,
  };
}

/** The ids a preview would actually change for `rule` (already-set rows are left out). */
export function idsToChange(matched: readonly PreviewMatch[], rule: PushSkuRule): string[] {
  return matched.filter((row) => row.currentRule !== rule).map((row) => row.id);
}

/**
 * A second preview (just the base garments added from a paste review) folded
 * into the first, so adding them never re-checks the whole list. Matched rows
 * are de-duplicated by id. The in-stock pair count is added up (null when
 * either side does not know it); like the server's own count, it is an upper
 * bound, which is all the pause warning needs.
 */
export function mergePreviews(base: PreviewResult, extra: PreviewResult): PreviewResult {
  const seen = new Set(base.matched.map((row) => row.id));
  const added = extra.matched.filter((row) => !seen.has(row.id));
  return {
    ...base,
    matched: [...base.matched, ...added],
    truncated: base.truncated || extra.truncated,
    inStockPairs:
      base.inStockPairs === null || extra.inStockPairs === null ? null : base.inStockPairs + extra.inStockPairs,
  };
}

export interface AlwaysZeroSummary {
  skuCount: number;
  baseGarmentCount: number;
  /** Sum of the base garments' Shopify variants; null when no count is known. */
  variantCount: number | null;
  /**
   * True when the sum is a floor: a base garment had no count, or SKUs that
   * are not base garments are in the batch too (their own variants are not
   * counted).
   */
  variantCountPartial: boolean;
  anyCacheCold: boolean;
}

/**
 * What the Always 0 confirm states. Only the rows that will change count:
 * a SKU already at Always 0 is not news.
 */
export function summarizeAlwaysZero(matched: readonly PreviewMatch[]): AlwaysZeroSummary {
  const changing = matched.filter((row) => row.currentRule !== 'always_zero');
  const baseGarments = changing.filter((row) => row.isBaseGarment);
  let variantCount: number | null = null;
  let unknownBase = false;
  for (const row of baseGarments) {
    if (typeof row.shopifyVariantCount === 'number') {
      variantCount = (variantCount ?? 0) + row.shopifyVariantCount;
    } else {
      unknownBase = true;
    }
  }
  const othersToo = changing.length > baseGarments.length;
  return {
    skuCount: changing.length,
    baseGarmentCount: baseGarments.length,
    variantCount,
    variantCountPartial: variantCount !== null && (unknownBase || othersToo),
    anyCacheCold: baseGarments.some((row) => row.cacheCold === true),
  };
}

/**
 * The Always 0 confirm's title: "Show 0 on 240 Shopify variants?" when base
 * garments carry a known variant count, else "Set 3 SKUs to Always 0?".
 */
export function alwaysZeroTitle(
  t: TFunction<'inventory'>,
  summary: AlwaysZeroSummary | null,
  requestedCount: number,
): string {
  if (summary && summary.baseGarmentCount > 0 && summary.variantCount !== null) {
    const values = { count: summary.variantCount, variants: summary.variantCount.toLocaleString() };
    return summary.variantCountPartial
      ? t('stockSync.pushRules.confirm.titleVariantsAtLeast', values)
      : t('stockSync.pushRules.confirm.titleVariants', values);
  }
  return t('stockSync.pushRules.confirm.title', { count: summary?.skuCount ?? requestedCount });
}

/**
 * Whether setting these SKUs to Always 0 will pause the push (the big-batch
 * pause) until someone approves it: the in-stock pairs pass the ceiling the
 * push worker computes from the SAVED Always 0 limit (`alwaysZeroCeiling`,
 * shared with the worker; no ceiling with No limit, which is 0). Unknown
 * counts never warn.
 */
export function willPauseThePush(
  preview: Pick<PreviewResult, 'inStockPairs'> | null,
  alwaysZeroLimit: number | undefined,
): boolean {
  if (!preview || preview.inStockPairs === null) return false;
  const ceiling = alwaysZeroCeiling({ alwaysZeroLimit });
  return ceiling !== null && preview.inStockPairs > ceiling;
}

/**
 * The calls that put rules back as they were before a change (Undo): one per
 * rule, 'none' for SKUs that had no rule. Always 0 last, so a failure part
 * way never leaves SKUs zeroed that were meant to be pushed.
 */
export function undoCalls(previous: readonly PreviousRule[]): Array<{ skuIds: string[]; rule: PushSkuRuleOrNone }> {
  const byRule = new Map<PushSkuRuleOrNone, string[]>();
  for (const entry of previous) {
    const rule: PushSkuRuleOrNone = entry.rule ?? 'none';
    const list = byRule.get(rule);
    if (list) list.push(entry.skuId);
    else byRule.set(rule, [entry.skuId]);
  }
  const order: PushSkuRuleOrNone[] = ['none', 'skip', 'always_zero'];
  return order
    .filter((rule) => (byRule.get(rule)?.length ?? 0) > 0)
    .map((rule) => ({ skuIds: byRule.get(rule)!, rule }));
}

/**
 * Where the section stands with the live push, read from the SAVED settings
 * (never the form's unsaved edits): the rules only take effect when a push
 * actually writes to Shopify. Halted is the most urgent, then off, then dry run.
 */
export type PushRunState = 'halted' | 'off' | 'dry_run' | null;

export function pushRunState(settings: {
  enabled: boolean;
  autoSyncEnabled: boolean;
  dryRunMode: boolean;
  halted: unknown;
}): PushRunState {
  if (settings.halted) return 'halted';
  if (!settings.enabled || !settings.autoSyncEnabled) return 'off';
  if (settings.dryRunMode) return 'dry_run';
  return null;
}
